'use client';

import { useState, useEffect } from 'react';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  roles: string[];
  permissions: string[];
  schoolId?: string;
}

interface School {
  id: string;
  name: string;
  logo?: string;
  domain: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
}

interface AuthData {
  user: User | null;
  school: School | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  logout: () => Promise<void>;
}

export function useAuth(): AuthData {
  const [user, setUser] = useState<User | null>(null);
  const [school, setSchool] = useState<School | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        
        // Get user data from localStorage
        const userStr = localStorage.getItem('user');
        const schoolStr = localStorage.getItem('school');
        const token = localStorage.getItem('token');

        if (!token || !userStr) {
          setIsAuthenticated(false);
          setUser(null);
          setSchool(null);
          return;
        }

        const userData = JSON.parse(userStr);
        let schoolData = schoolStr ? JSON.parse(schoolStr) : null;

        // If we have user data but no school data, try to fetch it
        if (userData && !schoolData && userData.schoolId) {
          try {
            const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1';
            const response = await fetch(`${apiUrl}/schools/${userData.schoolId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.ok) {
              const schoolResponse = await response.json();
              schoolData = schoolResponse.data || schoolResponse;
              localStorage.setItem('school', JSON.stringify(schoolData));
            } else {
              // Create fallback school data
              schoolData = {
                id: userData.schoolId,
                name: 'Demo School',
                domain: 'demo.school.com',
                logo: null,
                primaryColor: '#3B82F6',
                secondaryColor: '#1E40AF',
                accentColor: '#60A5FA'
              };
            }
          } catch (error) {
            // Create fallback school data
            schoolData = {
              id: userData.schoolId,
              name: 'Demo School',
              domain: 'demo.school.com',
              logo: null,
              primaryColor: '#3B82F6',
              secondaryColor: '#1E40AF',
              accentColor: '#60A5FA'
            };
          }
        }

        setUser(userData);
        setSchool(schoolData);

        const resolvedSchoolId = schoolData?.id || userData?.schoolId;
        if (resolvedSchoolId) {
          localStorage.setItem('school_id', resolvedSchoolId);
        } else {
          localStorage.removeItem('school_id');
        }

        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
        setSchool(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for storage changes (e.g., when user logs out in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        initializeAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const isSuperAdmin = user?.roles?.includes('super_admin') || false;
  const isAdmin = user?.roles?.some(role => 
    ['super_admin', 'admin', 'support_super_admin', 'support_admin'].includes(role)
  ) || false;

  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  };

  const hasPermission = (permission: string): boolean => {
    if (!user?.permissions) return false;
    return user.permissions.includes('*') || user.permissions.includes(permission);
  };

  const logout = async (): Promise<void> => {
    try {
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('school');
      localStorage.removeItem('school_id');
      
      // Reset state
      setUser(null);
      setSchool(null);
      setIsAuthenticated(false);
      
      // Optional: Call logout API endpoint
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1';
          await fetch(`${apiUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (error) {
          console.warn('Logout API call failed:', error);
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  return {
    user,
    school,
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    isAdmin,
    hasRole,
    hasPermission,
    logout,
  };
}
